import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface ScrollAnimationProps {
  children: ReactNode;
  animation?: 'fadeUp' | 'fadeIn' | 'scaleUp' | 'slideRight' | 'slideLeft';
  delay?: number;
  duration?: number;
  threshold?: number;
  className?: string;
  once?: boolean;
}

const ScrollAnimation: React.FC<ScrollAnimationProps> = ({
  children,
  animation = 'fadeUp',
  delay = 0,
  duration = 0.6,
  threshold = 0.1,
  className = '',
  once = true,
}) => {
  const { ref, inView } = useInView({
    triggerOnce: once,
    threshold,
  });

  const animations = {
    fadeUp: {
      visible: { 
        opacity: 1, 
        y: 0,
        transition: { 
          duration, 
          delay, 
          ease: [0.25, 0.1, 0.25, 1.0],  // Improved cubic-bezier curve
          opacity: { duration: duration * 1.2 }  // Slightly longer opacity transition
        }
      },
      hidden: { opacity: 0, y: 30 }
    },
    fadeIn: {
      visible: { 
        opacity: 1,
        transition: { 
          duration, 
          delay, 
          ease: [0.25, 0.1, 0.25, 1.0]
        }
      },
      hidden: { opacity: 0 }
    },
    scaleUp: {
      visible: { 
        opacity: 1, 
        scale: 1,
        transition: { 
          duration, 
          delay, 
          ease: [0.34, 1.56, 0.64, 1],  // Spring-like curve
          scale: { type: "spring", stiffness: 300, damping: 20 }
        }
      },
      hidden: { opacity: 0, scale: 0.92 }
    },
    slideRight: {
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { 
          duration, 
          delay, 
          ease: [0.25, 0.1, 0.25, 1.0],
          opacity: { duration: duration * 0.8, ease: "easeOut" }
        }
      },
      hidden: { opacity: 0, x: -40 }
    },
    slideLeft: {
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { 
          duration, 
          delay, 
          ease: [0.25, 0.1, 0.25, 1.0],
          opacity: { duration: duration * 0.8, ease: "easeOut" }
        }
      },
      hidden: { opacity: 0, x: 40 }
    }
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={animations[animation]}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default ScrollAnimation; 